import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "../Utils/useSEO";
import { motion } from "framer-motion";
import {
  BookOpen,
  Play,
  ExternalLink,
  AlertCircle,
} from "lucide-react";

const VIDEO_FILE = "Vendor Onboarding Workflow (1).mp4";
const VIDEO_URL = `https://d3gcyqrqkp5kxt.cloudfront.net/Supplier-Hub/Videos/${encodeURIComponent(VIDEO_FILE)}`;

const tutorials = [
  {
    id: "vendor-onboarding",
    title: "Vendor Onboarding",
    durationSeconds: 457,
    videoUrl: VIDEO_URL,
    manualPath: "/tutorials/user-manual",
  },
];

function formatDuration(totalSeconds) {
  const sec = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function TutorialVideo({ src, title, durationLabel }) {
  const videoRef = useRef(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setHasStarted(false);
    setErrorMessage("");
  }, [src]);

  const describeError = (video) => {
    const code = video?.error?.code;
    if (code === 4) return "This browser could not decode the video file.";
    if (code === 3) return "The video failed to decode in this browser.";
    if (code === 2) return "The video could not be loaded. Check your connection and try again.";
    return "The video could not be played in this browser.";
  };

  const startPlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    setErrorMessage("");
    const playPromise = video.play();
    if (playPromise && typeof playPromise.then === "function") {
      playPromise
        .then(() => setHasStarted(true))
        .catch((err) => {
          if (err?.name === "NotAllowedError") {
            setErrorMessage("Use the player controls to start the video.");
            return;
          }
          setErrorMessage(describeError(video) || "Playback was blocked. Open the video instead.");
        });
    } else {
      setHasStarted(true);
    }
  };

  return (
    <div className="w-full min-w-0">
      <div className="tutorial-video-frame border border-slate-200 dark:border-brand-orange/15">
        <video
          ref={videoRef}
          controls
          controlsList="nodownload"
          playsInline
          preload="metadata"
          title={title}
          onContextMenu={(event) => event.preventDefault()}
          onPlay={() => setHasStarted(true)}
          onError={(event) => setErrorMessage(describeError(event.currentTarget))}
          {...{ "webkit-playsinline": "true", "x-webkit-airplay": "allow" }}
        >
          <source src={src} type="video/mp4" />
        </video>

        {!hasStarted && !errorMessage && (
          <button
            type="button"
            onClick={startPlayback}
            aria-label={`Play ${title}`}
            className="absolute inset-x-0 top-0 bottom-10 flex flex-col items-center justify-center cursor-pointer group"
          >
            <span className="w-12 h-12 rounded-full bg-white/95 text-brand-orange flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
              <Play className="w-5 h-5 fill-current ml-0.5" />
            </span>
          </button>
        )}

        {!hasStarted && durationLabel && (
          <span className="tutorial-duration-badge">{durationLabel}</span>
        )}

        {errorMessage && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 bg-slate-950/90 text-center">
            <AlertCircle className="w-8 h-8 text-brand-orange" />
            <p className="text-sm text-white max-w-md">{errorMessage}</p>
            <a
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-orange text-white text-xs font-semibold hover:bg-brand-amber transition-colors"
            >
              Open video
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default function TutorialsPage() {
  useSEO({
    title: "Tutorials // Vendor Onboarding Walkthrough",
    description:
      "Watch Supply Spot tutorials, including the Vendor Onboarding workflow, and follow the key steps to get suppliers live on the platform.",
  });

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary transition-colors duration-300 overflow-x-hidden font-sans relative selection:bg-brand-orange/30 selection:text-text-primary">
      <main className="max-w-7xl mx-auto px-6 pt-8 sm:pt-10 pb-24 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="grid grid-cols-3 items-center mb-12 sm:mb-14"
        >
          <Link to="/" className="flex items-center select-none justify-self-start">
            <img src="/logo.png" alt="Supply Spot" className="h-8 sm:h-9 w-auto light-logo" />
            <img src="/logo-white.png" alt="Supply Spot" className="h-8 sm:h-9 w-auto dark-logo" />
          </Link>
          <h1 className="text-2xl sm:text-3xl font-semibold text-text-muted text-center tracking-tight font-title">
            Tutorials
          </h1>
          <div />
        </motion.div>

        <section className="flex flex-col gap-8 pb-16">
          {tutorials.map((tutorial) => (
            <div key={tutorial.id} className="tutorials-layout">
              <div className="tutorials-player">
                <TutorialVideo
                  src={tutorial.videoUrl}
                  title={tutorial.title}
                  durationLabel={formatDuration(tutorial.durationSeconds)}
                />
              </div>

              <div className="tutorials-sidebar text-left flex items-center gap-3 min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-text-primary font-title leading-snug whitespace-nowrap">
                  {tutorial.title}
                </h2>
                <span className="shrink-0 text-sm font-semibold text-text-muted">
                  {formatDuration(tutorial.durationSeconds)}
                </span>
              </div>

              <a
                href={tutorial.manualPath}
                target="_blank"
                rel="noopener noreferrer"
                className="tutorials-manual shrink-0 relative inline-flex group"
                aria-label={`${tutorial.title} user manual`}
              >
                <BookOpen className="w-8 h-8 text-red-500 stroke-[1.6] group-hover:text-red-600 transition-colors" />
                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-amber-400 text-white flex items-center justify-center text-[8px] font-bold border border-white">
                  i
                </span>
              </a>
            </div>
          ))}
        </section>
      </main>
    </div>
  );
}
